import type { WorkspaceSettings } from '../inviteUserUtils';
import type { AssemblyAccounts } from './accounts';
import type { AssemblyCurrency } from './currency';
import type { MemberRole, MemberState } from './flowTypes';

export type WorkspaceSlug = {
  shortCode: string;
  name: string;
};

export type Assembly = {
  assemblyId: string;
  createdAt: string;
  accounts: AssemblyAccounts;
  name: string;
  logo: string;
  domain: string;
  timeZone: string;
  flow: {
    creation: {
      value: string;
    };
  };
  workspaceSlug: WorkspaceSlug;
  whitelistedDomains: string[];
  currency: AssemblyCurrency;
  settings: WorkspaceSettings;
  exchangeRate: number;
  tangoDenominationRange?: {
    min: number | null;
    max: number | null;
  };
};

export type AssemblyBasicInfo = Pick<
  Assembly,
  'assemblyId' | 'logo' | 'name' | 'workspaceSlug'
>;

export type Workspace = {
  userId: string;
  inviteToken?: string;
  totalMembers: number;
  optedWorkspaceExperience?: OptedWorkspaceExperience;
} & AssemblyBasicInfo;

export type AssemblyInfoAPIResponse = {
  assemblyId: string;
  logo: string;
  name: string;
  workspaceSlug: WorkspaceSlug;
};

export type MemberAPIResponse = {
  member: Member;
  assembly: Assembly;
};

export enum OptedWorkspaceExperience {
  Legacy = 'V2',
  New = 'V3',
}

export enum AllowanceType {
  Regular = 'regular',
  Custom = 'custom',
  NoAllowance = 'no',
}

export enum AllowanceCycle {
  Monthly = 'monthly',
}

export type MemberPermissions = {
  canCreateFlow: boolean;
  canInviteNewUsers: boolean;
};

export type Member = {
  memberId: string;
  email: string;
  status: string;
  state: string;
  profile: Profile;
  managerIds: string[];
  reportIds: string[];
  timeZone: string;
  activatedAt: string;
  name: string;
  firstName: string;
  lastName: string;
  username: string;
  isFreeEmail: boolean;

  /** @deprecated avoid using this since some APIs don't return this value. use `memberId` instead */
  memberID: string;

  image?: string;
  pointsGiven: number;
  totalPointsGiven: number;
  memberState: MemberState;
  role: MemberRole[];
  pronouns?: string;
  optedWorkspaceExperience: OptedWorkspaceExperience;
  pointsEarned: number;
  pointsLeftThisCycle: number;
  allowance: {
    points: number;
    type: AllowanceType;
    cycle: AllowanceCycle;
  };
  permissions: MemberPermissions;
};

export type Profile = {
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  location: string;
  department: string;
  image: string;
  username: string;
  isManager: boolean;
  birthday: Birthday;
  hiredday: HiredDay;
  pronouns: string;
  isFirstLogin?: boolean;
  email?: string;
};

export type Birthday = {
  day?: number;
  month?: number;
  isVisibleAndCelebrated?: boolean;
};

export type HiredDay = {
  day?: number;
  month?: number;
  year?: number;
};

export type LegacyUserDetailsAPIResponse = {
  jwtToken: string;
  user: LegacyUserRepresentation;
};

export type LegacyUserRepresentation = {
  firstName: string;
  lastName: string;
  isFirstLogin: boolean;
  assemblyId: string;
  // we don't need these fields for now
  _id: string;
  email: string;
  // role: string[];
  // isFreeEmail: boolean;
};

export type ListWorkspacesAPIResponse = {
  email: string;
  canCreate: boolean;
  isLegacySession: boolean;
  allowList: Workspace[];
  invited: Workspace[];
  partOf: Workspace[];
};

export type UserDetails = {
  member: Member;
  assembly: Assembly & { workspaceSlugPath: string };
};

export enum AssemblyPlanType {
  Lite = 'Lite',
  Starter = 'Starter',
  Premium = 'Premium',
  Standard = 'Standard',
}

enum PlanStatus {
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
}

export type EntityPermissionApiResponse = {
  data: {
    entityId: string;
    canEndAnnouncement?: boolean;
    canEditAnnouncement?: boolean;
    canShareAsAnnouncement: boolean;
  }[];
};

export type PlanFeaturesAPIResponse = {
  name: AssemblyPlanType;
  features: {
    id: string;
    value: number;
    category: string;
    status: PlanStatus;
    isPaymentRequired: boolean;
  }[];
  uniqueId?: string;
};

export type DoraStatusAPIResponse = {
  isDoraReady: boolean;
};

type NullableCursor = string | null;

export type MetadataProps = {
  pagination: {
    cursor: {
      next: NullableCursor;
      previous: NullableCursor;
    };
  };
};
